/* Eloss — mobile sticky buy bar
 *
 * Spec: §F.2 ("Mobile: sticky buy bar appears when (9) scrolls out of view; it
 * contains thumbnail, price, and ATC, and scrolls to the pickers rather than
 * duplicating them"), §M (INP), §P.5.
 *
 * IntersectionObserver on the real add-to-cart button, not a scroll listener:
 * the bar's visibility is a fact about one element, and asking the browser
 * that question directly costs nothing per frame (§M, INP).
 *
 * The bar's button does NOT own a form. It submits the main product form
 * through the `form` attribute, so the page has exactly one form, one variant
 * input and one quantity — which is what "rather than duplicating them"
 * requires, and what keeps rule 10's whole-form FormData submit honest.
 *
 * Mobile only. The media query is evaluated here as well as in CSS because a
 * bar that is display:none must not be announced to a screen reader on
 * desktop either.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  const MOBILE = '(max-width: 989px)';

  Eloss.define(
    'eloss-sticky-atc',
    class ElossStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.root = document.querySelector(
          `.main-product[data-section-id="${this.sectionId}"]`
        );
        if (!this.root) return;

        this.anchor = this.root.querySelector(
          '.product-form .product-form__submit'
        );
        if (!this.anchor || !('IntersectionObserver' in window)) return;

        this.mq = window.matchMedia(MOBILE);
        this.onMediaChange = () => this.sync();
        this.mq.addEventListener('change', this.onMediaChange);

        this.observer = new IntersectionObserver(
          (entries) => {
            this.anchorVisible = entries[0].isIntersecting;
            this.sync();
          },
          { threshold: 0 }
        );
        this.observer.observe(this.anchor);

        this.onClick = (event) => this.handleClick(event);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.mq?.removeEventListener('change', this.onMediaChange);
        if (this.onClick) this.removeEventListener('click', this.onClick);
      }

      sync() {
        const shouldShow = this.mq.matches && this.anchorVisible === false;
        this.hidden = !shouldShow;
      }

      /**
       * §F.2: the bar "scrolls to the pickers rather than duplicating them".
       * Tapping the thumbnail or the title takes the visitor to the pickers;
       * the submit button is left alone so it can do its actual job.
       */
      handleClick(event) {
        if (event.target.closest('.product-form__submit')) return;
        const scrollTarget =
          this.root.querySelector('.variant-picker') ||
          this.root.querySelector('.product-form');
        if (!scrollTarget) return;

        event.preventDefault();
        scrollTarget.scrollIntoView({
          behavior: Eloss.prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center',
        });

        const firstControl = scrollTarget.querySelector(
          'input:not([disabled]), select:not([disabled])'
        );
        if (firstControl) firstControl.focus({ preventScroll: true });
      }
    }
  );
})();
